<template>
  <PanelCard size="big" class="timesheet-by-period">
    <div class="timesheet-summary">
      <div class="description">
        <div class="bold-txt">{{$t('timesheet.employee_panel_title')}}</div>
        <div class="discreet-txt">{{$t('timesheet.employee_panel_title_small_print')}}</div>
      </div>
      <div class="by-the-numbers">
        <div class="discreet-txt">{{ $t('timesheet.total_time_card') }}</div>
        <div class="duration">{{ sumEntriesAsDuration(this.value) }}</div>
      </div>
    </div>

    <div class="sub-period-container">
      <div v-for="period of entriesSlicedBySubPeriodAndDay" :key="`period_${period.identifier}`">
        <PanelCard
            @opened="focusedDay = day.date"
            size="small" collapsible :collapsed="focusedDay !== day.date"
            class="day-container"
            v-for="day of period.days"
            :key="`day_${day.date}`"
        >
          <!-- card header -->
          <template #header>
            <div class="header">
              <div class="title">{{ prettyDate(day.date) }}</div>
              <div style="flex-grow: 1"></div>
              <div class="discreet-txt">{{ $t('subTotal') }}</div>
              <div class="bold-txt">{{ sumEntriesAsDuration(day.entries) }}</div>
            </div>
          </template>
          <!-- card content -->
          <template #default>
            <div class="entries">
              <TimesheetEntry
                  v-for="(entry, i) of day.entries"
                  v-model="day.entries[i]"
                  :key="`entry_${entry.id}`"
                  @delete="deleteRow(entry.id)"
              />
            </div>
            <div class="add-punch" @click="addRow(day.date)">
              <div class="action">+</div>
              <div>{{$t('timesheet.add_entry')}}</div>
            </div>
          </template>

        </PanelCard>

      </div>
    </div>
  </PanelCard>
</template>

<script>

import moment from 'moment-timezone/index';
import TimesheetEntry from "@/components/qsr/TimesheetEntry";
import PanelCard from "@/components/PanelCard";
import Vue from "vue";

const MOMENT_DATE_FORMAT = 'YYYY-MM-DD';
const MOMENT_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

export default {
  name: "TimesheetEmployee",
  components: {TimesheetEntry, PanelCard},

  props: {
    value: {
      type: Array, /* List<TimeSheetEntry> */
      required: true
    },
    employee: {
      type: Object,
      required: true
    },
    companyId: {
      type: Number|String,
      required: false
    },
    periodMinDate: {
      type: String,
      required: true,
      validator: v => moment(v, MOMENT_DATE_FORMAT).isValid()
    },
    periodMaxDate: {
      type: String,
      required: true,
      validator: v => moment(v, MOMENT_DATE_FORMAT).isValid()
    }
  },

  data() {
    return {
      focusedDay: null,
    }
  },

  computed: {
    /* [{
     *   identifier: string,
     *   days: [{
     *     date: dateString,
     *     entries: List<TimeSheetEntry>
     *   }]
     * }]
     */
    entriesSlicedBySubPeriodAndDay() {
      /* Map<DateString, List<TimeSheet>>*/
      let entriesByDay = new Map();
      for (let entry of this.value) {
        if (entry.deleted) { continue; }
        let day = entry.start.substring(0, 10);
        if (!entriesByDay.has(day)) {
          entriesByDay.set(day, []);
        }
        entriesByDay.get(day).push(entry);
      }

      let tmp = [];
      let periodDurationInDays = 7;
      let minDate = this.periodMinDate;
      let maxDate = this.periodMaxDate;
      for (let m = moment(minDate, MOMENT_DATE_FORMAT); m.isSameOrBefore(maxDate); m.add(1, 'days')) {
        let subPeriodIndex = Math.floor(m.diff(minDate, 'days') / periodDurationInDays);
        if (!tmp[subPeriodIndex]) {
          tmp.push({
            identifier: `period ${subPeriodIndex}`,
            days: []
          });
        }
        let dateAsString = m.format(MOMENT_DATE_FORMAT);
        tmp[subPeriodIndex].days.push({
          date: dateAsString,
          entries: entriesByDay.get(dateAsString) || []
        });
      }
      return tmp;
    }
  },

  methods: {
    addRow(date) {
      this.value.push({
        id: `tmp_${Math.floor(Math.random()*100000)}`,
        employee: {id: this.employee.id},
        company: this.companyId,
        start: moment(date, MOMENT_DATE_FORMAT).format(MOMENT_DATE_TIME_FORMAT),
        end: moment(date, MOMENT_DATE_FORMAT).format(MOMENT_DATE_TIME_FORMAT)
      });
    },
    deleteRow(entryId) {
      let index = this.value.findIndex(e => e.id === entryId);
      if (index === -1) {
        return;
      }
      let entry = this.value[index];
      if (!!parseInt(entry.id)) {
        Vue.set(entry, 'deleted', true);
      } else {
        this.value.splice(index, 1);
      }
    },
    sumEntriesAsDuration(entries) {
      let diffInMinutes = entries.reduce((last, entry) => last + (entry.deleted ? 0 : this.diffInMinutes(entry.start, entry.end)), 0);
      return this.minutesToDuration(diffInMinutes);
    },
    diffInMinutes(fromDateString, toDateString) {
      let fromMoment = this.toMoment(fromDateString);
      let toMoment = this.toMoment(toDateString);
      return toMoment.diff(fromMoment, 'minutes');
    },
    minutesToDuration(minutes) {
      let hour = minutes > 0 ? Math.floor(minutes / 60) : Math.ceil(minutes / 60);
      let min = Math.abs(minutes % 60);
      return `${hour.toString().padStart(2,'0')}:${min.toString().padStart(2,'0')}`;
    },
    toMoment(str, format) {
      return moment(str, format || MOMENT_DATE_TIME_FORMAT);
    },
    prettyDate(date) {
      return moment(date, MOMENT_DATE_FORMAT).format('dddd, LL')
    }
  }
}
</script>

<style lang="scss" scoped>
.bold-txt {
  line-height: 19px;
  font-size: 18px;
  font-weight: bold;
  color: #404040;
}
.discreet-txt {
  line-height: 16px;
  font-size: 14px;
  color: #808080;
}
.timesheet-summary {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;

  .description {

  }
  .by-the-numbers {
    display: flex;
    align-items: flex-end;
    .duration {
      margin-left: 16px;
      font-weight: 900;
      font-size: 20px;
    }
  }
}
.sub-period-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  row-gap: 40px
}
.day-container {
  margin-bottom: 8px;

  .entries > * {
    margin-bottom: 8px;
  }
  .add-punch {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    border: 1px dashed #e5e5e5;
    height: 40px;
    text-align: center;
    margin-top: 10px;
    cursor: pointer;
    color: var(--primary-color);
    > :not(:last-child) {
      margin-right: 10px;
    }
  }
}
.header {
  display: flex;
  align-items: flex-end;
  > :not(:last-child) {
    margin-right: 16px;
  }
  .title {
    color: var(--primary-color);
    font-size: 14px;
    font-weight: bold;
  }
  .action {
    cursor: pointer;
    width: 20px;
    height: 100%;
    text-align: center;
  }
}
</style>
